"use client";

import { motion, type Variants } from "framer-motion";
import { useInView } from "react-intersection-observer";
import type { CSSProperties, ReactNode } from "react";
import { useMotionPreference } from "@/hooks/use-motion-preference";

const EASE = [0.22, 1, 0.36, 1] as const;

interface RevealProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Seconds before the animation starts once in view. */
  delay?: number;
  /** Travel distance in px (positive = rises up). */
  y?: number;
  /** Animate only the first time it enters (default true). */
  once?: boolean;
}

/**
 * The single easing primitive for the site: react-intersection-observer detects
 * the viewport entry, Motion does the ease-in. One lib for detection, one for
 * easing — no scattered `whileInView`. Reduced-motion users get the static node.
 */
export function Reveal({ children, className, style, delay = 0, y = 24, once = true }: RevealProps) {
  const { reducedMotion } = useMotionPreference();
  const { ref, inView } = useInView({ triggerOnce: once, rootMargin: "0px 0px -10% 0px" });

  if (reducedMotion) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.6, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

export const revealItemVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
};

interface RevealGroupProps {
  children: ReactNode;
  className?: string;
  stagger?: number;
  once?: boolean;
}

/**
 * Staggered container — wrap children in `<RevealItem>` and they cascade in as
 * the group scrolls into view.
 */
export function RevealGroup({ children, className, stagger = 0.1, once = true }: RevealGroupProps) {
  const { reducedMotion } = useMotionPreference();
  const { ref, inView } = useInView({ triggerOnce: once, rootMargin: "0px 0px -10% 0px" });

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren: 0.05 } },
      }}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
    >
      {children}
    </motion.div>
  );
}

export function RevealItem({ children, className }: { children: ReactNode; className?: string }) {
  const { reducedMotion } = useMotionPreference();
  if (reducedMotion) return <div className={className}>{children}</div>;
  return (
    <motion.div className={className} variants={revealItemVariants}>
      {children}
    </motion.div>
  );
}

interface PopTextProps {
  children: ReactNode;
  className?: string;
  /** Seconds before it pops, once in view — use to stage lines in sequence. */
  delay?: number;
  once?: boolean;
  /** Scale origin — "left" for left-aligned titles, "center" for centered ones. */
  align?: "left" | "center" | "right";
}

/**
 * Announcement-style headline reveal: the text pops in — scaling up from small
 * with a slight overshoot — and de-blurs. Viewport-triggered, so it fires on load
 * for above-the-fold titles (hero) and on scroll for lower headings. Stage
 * multiple lines by giving each an increasing `delay`.
 */
export function PopText({
  children,
  className,
  delay = 0,
  once = true,
  align = "center",
}: PopTextProps) {
  const { reducedMotion, isMobile } = useMotionPreference();
  const { ref, inView } = useInView({ triggerOnce: once, rootMargin: "0px 0px -10% 0px" });

  // Bottom padding gives stacked title lines headroom for descenders.
  if (reducedMotion)
    return (
      <span className={className} style={{ paddingBottom: "0.1em" }}>
        {children}
      </span>
    );

  // `filter: blur()` is costly to animate on mobile Safari, so phones get the
  // pop with opacity + scale only and the blur pinned to 0. We still pass the
  // `filter` key (at 0) so framer always reconciles it: the SSR/first-render
  // `initial` assumes desktop and applies blur(8px), and if the mobile target
  // omitted `filter` that initial blur would be stranded — leaving the title
  // permanently fuzzy.
  const hidden = isMobile
    ? { opacity: 0, scale: 0.8, filter: "blur(0px)" }
    : { opacity: 0, scale: 0.72, filter: "blur(8px)" };
  const shown = isMobile
    ? { opacity: 1, scale: [0.8, 1.04, 1], filter: "blur(0px)" }
    : { opacity: 1, scale: [0.72, 1.06, 1], filter: "blur(0px)" };

  return (
    <motion.span
      ref={ref}
      className={className}
      initial={hidden}
      animate={inView ? shown : hidden}
      transition={{
        duration: 0.7,
        ease: EASE,
        delay,
        scale: { duration: 0.7, ease: EASE, delay, times: [0, 0.62, 1] },
      }}
      style={{
        transformOrigin: `${align} center`,
        // No hardcoded `will-change`: a permanently promoted layer makes iOS
        // Safari render text from a low-res cached texture (the "blurry text"
        // bug). Framer toggles will-change around the animation itself.
        paddingBottom: "0.1em",
      }}
    >
      {children}
    </motion.span>
  );
}
